import { useEffect, useMemo, useRef, useState } from 'react'
import './RangeFilter.css'

/**
 * The date range every figure on a dashboard is measured over.
 *
 * The design's control, on both dashboards: seven presets and a custom range,
 * behind one button that shows the current selection. The onboarding tour calls
 * it out on both screens with the same sentence — "every KPI and chart below
 * responds to this" — which is the whole reason it is one shared component
 * rather than two similar ones.
 *
 * ## Why "Last 28 days" and not 30
 *
 * The design's wording, and it is the better one for anything with a weekly
 * rhythm: 28 days is exactly four weeks, so week-on-week comparisons do not
 * drift and a Monday is always compared against the same number of Mondays.
 *
 * ## Boundaries are days, not moments
 *
 * A range runs from the start of its first day to the end of its last, in local
 * time. "Last 7 days" measured from this instant would move every time a page
 * re-rendered and would quietly exclude part of today.
 */

export type RangeKey =
  | 'd7'
  | 'd28'
  | 'd90'
  | 'm12'
  | 'ytd'
  | 'lastYear'
  | 'all'
  | 'custom'

export interface Range {
  key: RangeKey
  label: string
  /** Epoch ms, inclusive. Null means no lower bound — all time. */
  from: number | null
  /** Epoch ms, exclusive. */
  to: number
}

const PRESETS: { key: RangeKey; label: string }[] = [
  { key: 'd7', label: 'Last 7 days' },
  { key: 'd28', label: 'Last 28 days' },
  { key: 'd90', label: 'Last 90 days' },
  { key: 'm12', label: 'Last 12 months' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'lastYear', label: 'Last year' },
  { key: 'all', label: 'All time' },
]

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return startOfDay(d)
}

export function rangeFor(key: RangeKey, customStart = '', customEnd = ''): Range {
  const now = new Date()
  const endOfToday = startOfDay(now) + 86_400_000
  const label = PRESETS.find((p) => p.key === key)?.label ?? 'Custom'

  switch (key) {
    case 'd7':
      return { key, label, from: daysAgo(6), to: endOfToday }
    case 'd28':
      return { key, label, from: daysAgo(27), to: endOfToday }
    case 'd90':
      return { key, label, from: daysAgo(89), to: endOfToday }
    case 'm12': {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 1)
      return { key, label, from: startOfDay(d), to: endOfToday }
    }
    case 'ytd':
      return { key, label, from: new Date(now.getFullYear(), 0, 1).getTime(), to: endOfToday }
    case 'lastYear':
      return {
        key,
        label,
        from: new Date(now.getFullYear() - 1, 0, 1).getTime(),
        to: new Date(now.getFullYear(), 0, 1).getTime(),
      }
    case 'all':
      return { key, label, from: null, to: endOfToday }
    case 'custom': {
      const from = customStart ? Date.parse(`${customStart}T00:00:00`) : null
      // Exclusive end, so a custom range that names a day includes that day.
      const to = customEnd ? Date.parse(`${customEnd}T00:00:00`) + 86_400_000 : endOfToday
      return {
        key,
        label: customStart || customEnd ? `${customStart || '…'} to ${customEnd || 'today'}` : 'Custom',
        from: Number.isNaN(from as number) ? null : from,
        to: Number.isNaN(to) ? endOfToday : to,
      }
    }
  }
}

export function RangeFilter({
  value,
  onChange,
}: {
  value: Range
  onChange: (r: Range) => void
}) {
  const [open, setOpen] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A custom range applies as soon as it is a range. Waiting for an Apply
  // button means a half-typed date sits there looking like it counts.
  const custom = useMemo(() => rangeFor('custom', customStart, customEnd), [customStart, customEnd])

  return (
    <div className="rangef" ref={wrap}>
      <button className="rangef__btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span aria-hidden="true">📅</span> {value.label} ▾
      </button>

      {open && (
        <div className="rangef__menu" role="menu">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`rangef__item${value.key === p.key ? ' rangef__item--on' : ''}`}
              onClick={() => {
                onChange(rangeFor(p.key))
                setOpen(false)
              }}
            >
              {p.label}
            </button>
          ))}

          <div className="rangef__custom">
            <div className="rangef__customlabel">Custom range:</div>
            <input
              className="input"
              type="date"
              value={customStart}
              aria-label="Range start"
              onChange={(e) => {
                setCustomStart(e.target.value)
                onChange(rangeFor('custom', e.target.value, customEnd))
              }}
            />
            <input
              className="input"
              type="date"
              value={customEnd}
              aria-label="Range end"
              onChange={(e) => {
                setCustomEnd(e.target.value)
                onChange(rangeFor('custom', customStart, e.target.value))
              }}
            />
            {value.key === 'custom' && custom.from === null && (
              <p className="rangef__hint">Pick a start date, or this counts everything up to the end.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
